"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Instagram,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  Camera,
  Hash,
  Eye,
  Gift,
  BarChart3,
  Bot,
  Calendar,
  TrendingUp,
  Sparkles,
  Filter,
  Image as ImageIcon,
  Clock,
  ArrowLeft,
  PlayCircle,
  ShieldCheck,
  Zap,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
  Marquee,
} from "@/components/landing/solutions/SolutionSections";

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>Instagram‑First · مخصوص پیج‌های فارسی</HeroBadge>
            <HeroTitle pre="اینستاگرام،" gradient="خودکار و حرفه‌ای" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              پاسخ خودکار دایرکت و کامنت، قرعه‌کشی منصفانه، آنالیز عمیق پست‌ها و استوری‌ها، صف ارسال
              و گزارش حرفه‌ای — همه در یک پنل فارسی.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                اتصال پیج{" "}
                <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              </Link>
              <a
                href="#mockup"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center group-hover:bg-foreground/10">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">دموی زنده</span>
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> مطابق سیاست‌های Meta
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-secondary-main" /> راه‌اندازی ۲ دقیقه
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={13} className="text-primary-main" /> پاسخ زیر ۱۰ ثانیه
              </span>
            </div>
          </div>
          <div id="mockup">
            <InstagramPhone />
          </div>
        </div>
      </section>

      <GiantStats
        items={[
          { value: "+۲.۴M", label: "پیام پاسخ‌داده‌شده", sub: "تا امروز" },
          { value: "%۹۸", label: "نرخ پاسخ زیر ۱ دقیقه" },
          { value: "+۵٬۲۰۰", label: "پیج فعال" },
          { value: "۲۴/۷", label: "پایش بدون استراحت" },
        ]}
      />

      {/* Line features (no card spam) */}
      <Section>
        <SectionHeader
          eyebrow="ابزارهای اینستاگرام"
          title="هر چه برای پیج حرفه‌ای لازم دارید"
          desc="از پاسخ خودکار تا آنالیز عمیق محتوا، همه چیز در یک محیط فارسی روان."
        />
        <LineFeatures
          items={[
            {
              icon: MessageCircle,
              title: "پاسخ دایرکت با AI",
              desc: "ترندی پیام مشتری را می‌فهمد، با لحن برند پاسخ می‌دهد و در صورت نیاز به اپراتور پاس می‌دهد.",
            },
            {
              icon: Hash,
              title: "تریگر کامنت / هشتگ / منشن",
              desc: "روی کلمات کلیدی، هشتگ یا منشن خاص، خودکار دایرکت یا پاسخ کامنت بفرست.",
            },
            {
              icon: Gift,
              title: "قرعه‌کشی منصفانه",
              desc: "شرایط شرکت، فیلتر فیک، انتخاب تصادفی شفاف و انتشار خودکار برندگان.",
            },
            {
              icon: BarChart3,
              title: "آنالیز عمیق پست و استوری",
              desc: "نرخ تعامل، بهترین ساعات، الگوی محتوا و مقایسه با رقبا.",
            },
            {
              icon: Calendar,
              title: "تقویم محتوا و زمان‌بند",
              desc: "استوری، پست، ریلز و کاروسل را پیش‌نویس و زمان‌بندی کن.",
            },
            {
              icon: Filter,
              title: "فیلتر اسپم و توهین",
              desc: "کامنت‌های منفی و اسپم را خودکار مخفی، حذف یا ریپورت می‌کند.",
            },
          ]}
        />
      </Section>

      {/* Split — AI replies */}
      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="هوش مصنوعی"
          title={
            <>
              وقتی <span className="brand-gradient-text">آفلاینی</span>، ترندی آنلاینه
            </>
          }
          desc="ترندی روی محصولات، قیمت و لحن برند تو آموزش دیده. پاسخ‌ها کوتاه، انسانی و دقیقند."
          bullets={[
            "پاسخ شخصی‌شده با نام مشتری",
            "ارسال خودکار لینک پرداخت",
            "پاس به اپراتور وقتی AI شک داشت",
          ]}
          slot={<MockMiniChat />}
        />
      </Section>

      {/* Split — Giveaway */}
      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="قرعه‌کشی"
          title={
            <>
              قرعه‌کشی <span className="brand-gradient-text">شفاف</span> = اعتماد بیشتر
            </>
          }
          desc="شرایط شرکت‌کننده را تعریف کن، فیک‌ها فیلتر می‌شوند و برنده زنده مشخص می‌شود."
          bullets={[
            "شرط فالو + لایک + تگ",
            "حذف اکانت‌های فیک و خاموش",
            "انتشار ویدیوی استوری برنده",
          ]}
          slot={<MockGiveaway />}
        />
      </Section>

      {/* Steps */}
      <Section className="border-t border-border">
        <SectionHeader eyebrow="در ۴ گام" title="پیج‌ت روی اتوپایلوت" />
        <StepsTimeline
          steps={[
            { title: "اتصال پیج", desc: "بدون نیاز به API key، یک کلیک." },
            { title: "آموزش ترندی", desc: "محصولات، قیمت، آدرس و FAQ." },
            { title: "تنظیم فلوها", desc: "Drag-and-drop، بدون کد." },
            { title: "رشد و آنالیز", desc: "گزارش هوشمند و بهینه‌سازی." },
          ]}
        />
      </Section>

      {/* Compare */}
      <Section className="border-t border-border">
        <SectionHeader eyebrow="چرا ترندی؟" title="در برابر ربات‌های قدیمی" accent="secondary" />
        <CompareSplit
          ours={[
            "درک عمیق فارسی محاوره",
            "قرعه‌کشی با فیلتر فیک واقعی",
            "آنالیز عمیق و ساعات بهینه",
            "صف ارسال امن و آنتی‌بن",
            "اتصال بومی به CRM و SMS",
            "راه‌اندازی در ۲ دقیقه",
          ]}
          theirs={[
            "پاسخ کلمه‌به‌کلمه و خشک",
            "قرعه‌کشی بدون اعتبار",
            "فقط لایک و فالو می‌شمارد",
            "ریسک بن بالا",
            "ابزار جزیره‌ای",
            "ساعت‌ها تنظیمات",
          ]}
        />
      </Section>

      {/* Big Quote */}
      <Section>
        <BigQuote
          quote="یک شب پاسخ‌گویی نکنیم؛ ترندی شب‌ها هم سفارش می‌گیره. اولین ابزاریه که واقعاً فارسی محاوره رو می‌فهمه."
          name="سارا محمدی"
          role="بوتیک ساتین · تهران"
        />
      </Section>

      {/* Testimonials small */}
      <Section className="border-t border-border">
        <SectionHeader eyebrow="مشتریان ما" title="پیج‌هایی که جلو زدند" />
        <MiniTestimonials
          items={[
            {
              name: "علی رضایی",
              role: "آکادمی برنامه‌نویسی",
              quote: "نرخ تبدیل دایرکت به ثبت‌نام ۳ برابر شد.",
            },
            {
              name: "نگار حسینی",
              role: "آرایشی هیلدا",
              quote: "قرعه‌کشی‌های ترندی شفاف‌اند؛ مشتری اعتماد می‌کنه.",
            },
            {
              name: "بهنام شریفی",
              role: "آژانس آرکا",
              quote: "برای ۱۲ پیج مشتری از یک پنل کار می‌کنیم.",
            },
          ]}
        />
      </Section>

      {/* FAQ */}
      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="هر چه باید بدانید" />
        <FAQ
          items={[
            {
              q: "آیا پیج من بن می‌شود؟",
              a: "نه. ترندی از API رسمی Meta استفاده می‌کند و تمام محدودیت‌های نرخ ارسال را رعایت می‌کند.",
            },
            {
              q: "آیا چند پیج می‌توانم وصل کنم؟",
              a: "بله، در پلن‌های حرفه‌ای می‌توانید چند پیج با تیم‌های جدا مدیریت کنید.",
            },
            {
              q: "ترندی فارسی محاوره را می‌فهمد؟",
              a: "بله، روی فارسی محاوره و دیالکت‌ها آموزش دیده و لحن برند شما را تقلید می‌کند.",
            },
            {
              q: "اگر AI پاسخ را ندانست چه؟",
              a: "خودکار به اپراتور انسانی پاس می‌دهد و سؤال در «شکاف‌های دانشی» ثبت می‌شود.",
            },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            پیج‌ت آماده‌ست <span className="brand-gradient-text">جدی‌تر</span> رشد کنه؟
          </>
        }
        desc="همین الان وصل کن و اولین پاسخ خودکار رو در کمتر از ۵ دقیقه ببین."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

/* ====== Mockups (dark, brand-colored) ====== */
function InstagramPhone() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
      className="relative mx-auto w-full max-w-[340px]"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-60" />
      <div className="relative rounded-[2.5rem] border-[10px] border-border bg-background shadow-2xl overflow-hidden">
        <div className="bg-foreground/[0.05] border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-main to-secondary-main" />
            <div>
              <div className="text-xs font-black text-foreground">trendi.shop</div>
              <div className="text-[10px] text-primary-main">آنلاین</div>
            </div>
          </div>
          <Camera size={16} className="text-muted-foreground" />
        </div>
        <div className="p-4 space-y-3 min-h-[360px]">
          <UserBubble>قیمت کیف چرمی مدل آوا چقدره؟</UserBubble>
          <AiBubble>سلام 🌸 قیمت کیف آوا ۱.۲۸۰.۰۰۰ تومنه و الان ۱۵٪ تخفیف داره.</AiBubble>
          <UserBubble>سفارش می‌خوام</UserBubble>
          <AiBubble>
            عالی! لینک پرداخت آماده شد 👇
            <br />
            <span className="inline-block mt-1 px-2 py-1 rounded-md bg-primary-main/20 text-primary-main text-[10px] font-black">
              trendi.shop/p/2049
            </span>
          </AiBubble>
        </div>
        <div className="bg-foreground/[0.05] border-t border-border p-2 flex items-center justify-around text-muted-foreground">
          <Heart size={16} />
          <MessageCircle size={16} />
          <Send size={16} />
          <Bookmark size={16} />
        </div>
      </div>
      <div className="absolute -top-4 -right-4 px-3 py-2 rounded-xl bg-background border border-border shadow-xl text-[11px] font-bold text-foreground flex items-center gap-1.5">
        <Clock size={12} className="text-primary-main" /> پاسخ در ۸ ثانیه
      </div>
      <div className="absolute -bottom-4 -left-4 px-3 py-2 rounded-xl bg-background border border-border shadow-xl text-[11px] font-bold text-foreground flex items-center gap-1.5">
        <TrendingUp size={12} className="text-secondary-main" /> +۳۲٪ تبدیل
      </div>
    </motion.div>
  );
}

function MockMiniChat() {
  return (
    <div className="relative rounded-3xl border border-border bg-foreground/[0.04] p-6 backdrop-blur-sm">
      <div className="space-y-3">
        <UserBubble>سفارش ۱۲۹ من چی شد؟</UserBubble>
        <AiBubble>سفارش‌ت در حال ارسال هست 🚚 پیش‌بینی تحویل: فردا ۱۲ ظهر</AiBubble>
        <UserBubble>سایز L دارید؟</UserBubble>
        <AiBubble>بله، ۳ عدد موجوده ✨ می‌خواید رزرو کنم؟</AiBubble>
      </div>
      <div className="absolute -top-3 -right-3 px-2 py-1 rounded-md bg-secondary-main text-foreground text-[10px] font-black">
        AI
      </div>
    </div>
  );
}

function MockGiveaway() {
  return (
    <div className="relative rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <Gift size={16} className="text-secondary-main" /> قرعه‌کشی #تخفیف_عید
      </div>
      <div className="space-y-2.5">
        {[
          { n: "@narges_h", v: true },
          { n: "@bot_user_2k", v: false },
          { n: "@ali.r.tech", v: true },
          { n: "@fake_acc_99", v: false },
          { n: "@studio_arta", v: true },
        ].map((p) => (
          <div
            key={p.n}
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-foreground/[0.04] border border-border/60"
          >
            <span className="text-sm text-foreground">{p.n}</span>
            <span
              className={`text-[10px] font-black px-2 py-0.5 rounded-full ${p.v ? "bg-primary-main/20 text-primary-main" : "bg-foreground/5 text-muted-foreground/80 line-through"}`}
            >
              {p.v ? "تأیید" : "فیک"}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">برنده تصادفی</span>
        <span className="text-sm font-black text-secondary-main">@studio_arta 🎉</span>
      </div>
    </div>
  );
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="bg-foreground/[0.08] border border-border text-foreground text-xs px-3 py-2 rounded-2xl rounded-tr-sm max-w-[80%]">
        {children}
      </div>
    </div>
  );
}
function AiBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-end gap-1.5">
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-main to-secondary-main grid place-items-center shrink-0">
        <Sparkles size={11} className="text-foreground" />
      </div>
      <div className="bg-primary-main/10 border border-primary-main/30 text-foreground text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[80%]">
        {children}
      </div>
    </div>
  );
}

export default Page;
